/* eslint-disable @next/next/no-img-element */
import { FC, useState, useEffect, useRef } from "react";
import cx from "classnames";
import { useFormik } from "formik";
import { MobileModal } from "@/components/Modal";
import { MobilePaperTitle } from "@/components/PageTitle";
import Button, { IconButton } from "@/components/Button";
import { usePersonalDetailsQuery } from "@/fetch/profiles";
import Divider from "@/components/Divider";
import { CameraIcon, PictureIcon, BinIcon } from "@/components/Icon";
import RichEditor from "@/components/RichEditor";
import TextField from "@/components/TextField";
import { MediaUploader, UploaderFile } from "@/components/Uploader";
import {
  UploadMediaType,
  useCreatePostMutation,
  useFinalizePostMutation,
  useRecentPostsQuery,
  useUploadPostMedia,
} from "@/fetch/social";
import { useTrackers, useBrowserSettings } from "@/hooks";
import * as yup from "yup";
import Avatar from "@/components/Avatar";
import styles from "./CreatePostMobileDialog.module.scss";
import Grid from "@/components/Grid";
import {
  inputClasses,
  OutlinedInput,
  outlinedInputClasses,
} from "@mui/material";
import CreatePostMobileDialogCloseConfirmation from "./CreatePostMobileDialogCloseConfirmation";

type FormType = {
  content: string;
  media: Array<{ type: UploadMediaType; file: UploaderFile }>;
};

const MediaPreviewItem: React.FC<{
  preview: string | undefined;
  type: UploadMediaType;
  onDelete: () => void;
  disabled: boolean;
  totalMedia: number;
  mediaNumber: number;
}> = ({ preview, type, onDelete, disabled, totalMedia, mediaNumber }) => {
  console.log(totalMedia);
  console.log(mediaNumber);
  console.log(
    ((totalMedia < 4 && mediaNumber === 1) ||
      (totalMedia === 4 && mediaNumber < 3)) &&
      totalMedia !== 1
  );
  return (
    <div
      className={cx(styles.itemContainer, {
        [styles.leftItem]:
          ((totalMedia < 4 && mediaNumber === 1) ||
            (totalMedia === 4 && mediaNumber < 3) ||
            (totalMedia > 4 && mediaNumber % 2 !== 0)) &&
          totalMedia !== 1,
        [styles.rightItem]:
          (totalMedia < 4 && mediaNumber > 1) ||
          (totalMedia === 4 && mediaNumber > 2) ||
          (totalMedia > 4 && mediaNumber % 2 === 0),
        [styles.topItem]:
          (totalMedia === 3 && mediaNumber === 2) ||
          (totalMedia === 4 && (mediaNumber === 1 || mediaNumber === 3)) ||
          totalMedia > 4,
        [styles.bottomItem]:
          (totalMedia === 3 && mediaNumber === 3) ||
          (totalMedia === 4 && (mediaNumber === 2 || mediaNumber === 4)),
        // ||
        // (totalMedia > 4 && mediaNumber > 2),
        // [styles.leftItem]: totalMedia > 4 && mediaNumber % 2 !== 0,
        // [styles.bottomItem]: totalMedia > 4 && mediaNumber > 4,
        // [styles.rightItem]:
        //   totalMedia > 4 && mediaNumber % 2 === 0 && mediaNumber > 4,
      })}
    >
      <div className={styles.editRibbon}>
        {/* <IconButton className={styles.editButton} size="small">
            <EditIcon fontSize="small" />
          </IconButton> */}
        <IconButton
          className={styles.editButton}
          size="small"
          onClick={onDelete}
          disabled={disabled}
        >
          <BinIcon
            variant="outlined"
            fontSize="small"
            color={"inherit"}
            className={styles.binIcon}
          />
        </IconButton>
      </div>
      {type === UploadMediaType.Image ? (
        <img
          alt="image"
          src={preview}
          className={cx(styles.mediaPreview, {
            [styles.oneMedia]: totalMedia === 1,
            [styles.leftMedia]:
              ((totalMedia < 4 && mediaNumber === 1) ||
                (totalMedia === 4 && mediaNumber < 3)) &&
              totalMedia !== 1,
            [styles.rightMedia]:
              (totalMedia < 4 && mediaNumber > 1) ||
              (totalMedia === 4 && mediaNumber > 2),
            [styles.topMedia]:
              (totalMedia === 3 && mediaNumber === 2) ||
              (totalMedia === 4 && (mediaNumber === 1 || mediaNumber === 3)),
          })}
        />
      ) : (
        <>
          <video
            width="160px"
            height="160px"
            src={preview}
            className={styles.mediaPreview}
          />
          <span className={styles.videoPreviewItemOverlay} />
        </>
      )}
    </div>
  );
};

type Props = {
  onClose: () => void;
  onCreatePost?: () => Promise<any>;
};

const CreatePostMobileDialog: FC<Props> = ({ onClose, onCreatePost }) => {
  const editorRef = useRef<any>(null);
  const [isRefetchingPosts, setIsRefetchingPosts] = useState(false);
  const [isCloseAlertDialogOpen, setIsCloseAlertDialogOpen] = useState(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [openPicker, setOpenPicker] = useState<boolean>(false);
  const [isInputCommentFocused, setIsInputCommentFocused] =
    useState<boolean>(false);
  const {
    mutateAsync: createPost,
    isLoading: isPosting,
    cancel: cancelCreatePost,
  } = useCreatePostMutation();
  const {
    mutateAsync: finalizePost,
    isLoading: isFinalizing,
    cancel: cancelFinalizePost,
  } = useFinalizePostMutation();
  const { refetch: refetchRecentPosts } = useRecentPostsQuery(
    { pinned: false, adminPosts: false },
    {
      enabled: false,
    }
  );
  const {
    uploadProfilePicture,
    cancelUploadRequest,
    isLoading: isUploading,
    progress,
  } = useUploadPostMedia();
  const { track } = useTrackers();
  const { isAndroid } = useBrowserSettings();
  const { data: personalDetailsData, isLoading: isPersonalDetailsLoading } =
    usePersonalDetailsQuery();

  const profilePicturePath = personalDetailsData?.traveller?.s3_profile_pic;

  const isLoading =
    isPosting || isFinalizing || isRefetchingPosts || isUploading;

  const initialValues = {
    content: "",
    media: [],
  } as FormType;

  const validationSchema = yup.object({
    content: yup.string().required("Post content can not be empty."),
  });

  useEffect(() => {
    if (isInputCommentFocused) {
      const scrollY = window.scrollY;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
    } else {
      const scrollY = document.body.style.top;

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
    }
  }, [isInputCommentFocused]);

  const onSubmit = async (values: FormType) => {
    try {
      const createdPostResult = await createPost({ content: values.content });

      if (createdPostResult?.id) {
        for (let index = 0; index < values.media.length; index++) {
          setCurrentUploadIndex(index + 1);
          const medium = values.media[index];
          await uploadProfilePicture({
            file: medium.file,
            type: medium.type,
            postId: createdPostResult?.id,
          });
        }

        await finalizePost({
          id: createdPostResult.id,
          content: createdPostResult.content,
        });
        track("Post Created", {
          postId: createdPostResult?.id,
          eventId: "post-created",
          numberOfMedia: values.media.length || 0,
        });
      }
      setIsRefetchingPosts(true);
      await refetchRecentPosts();
      if (onCreatePost) {
        await onCreatePost?.();
      }
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsRefetchingPosts(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const onFileSelect = (acceptedFiles: UploaderFile[]) => {
    setOpenPicker(false);
    const transformedFiles = acceptedFiles.map((item) => ({
      file: item,
      type: item.type.includes("video")
        ? UploadMediaType.Video
        : UploadMediaType.Image,
    }));

    formik.setValues((values) => {
      const newMedia = values.media.concat(transformedFiles);
      return {
        ...values,
        media: newMedia,
      };
    });
  };

  useEffect(() => {
    return () => {
      cancelUploadRequest();
      cancelCreatePost();
      cancelFinalizePost();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEditorChange = (text: string) => {
    formik.setValues((values) => ({ ...values, content: text }));
  };

  const onMediaDelete = (index: number) => {
    formik.setValues((values) => {
      const newMedia = values.media.filter((item, i) => index !== i);
      return {
        ...values,
        media: newMedia,
      };
    });
  };

  const onDialogCloseTrigger = () => {
    if (formik.values.content.length || formik.values.media.length)
      setIsCloseAlertDialogOpen(true);
    else onClose();
  };

  return (
    <MobileModal>
      <MobilePaperTitle
        title="New post"
        iconType="close"
        onGoBack={onDialogCloseTrigger}
      >
        <Button
          size="small"
          className={styles.postButton}
          disabled={isLoading || !Boolean(formik.values.content.length)}
          onClick={() => formik.handleSubmit()}
        >
          {isLoading ? "Posting..." : "Post"}
        </Button>
      </MobilePaperTitle>
      <div className={styles.content}>
        <div className={styles.textContent}>
          <Avatar
            size="s"
            cloudinaryPath={profilePicturePath}
            className={styles.avatar}
          />
          {isAndroid ? (
            <TextField
              sx={{ width: "100%" }}
              multiline
              placeholder="What’s going on today?"
              rows={5}
              error={formik.touched.content && Boolean(formik.errors.content)}
              helperText={formik.touched.content && formik.errors.content}
              disabled={isLoading}
              value={formik.values.content}
              onChange={(event) => onEditorChange(event.target.value)}
              className={styles.textfield}
              inputProps={{ className: styles.textfield }}
              onFocus={() => setIsInputCommentFocused(true)}
              onBlur={() => setIsInputCommentFocused(false)}
            />
          ) : (
            <RichEditor
              placeholder="What’s going on today?"
              hasSuggestions
              disabled={isLoading}
              onChange={onEditorChange}
              error={formik.touched.content ? formik.errors.content : ""}
              touched={formik.touched.content}
              ref={(ref) => (editorRef.current = ref)}
              classes={{
                emojiPickerButton: styles.emojiButton,
                editor: styles.editorRoot,
                root: styles.editorRoot,
              }}
              onFocus={() => setIsInputCommentFocused(true)}
              onBlur={() => setIsInputCommentFocused(false)}
            />
          )}
        </div>
        <div
          className={cx(styles.mediaContainer, {
            [styles.moreMediaContainer]: formik.values.media.length > 4,
          })}
        >
          {formik.values.media?.map((medium, index) => (
            <MediaPreviewItem
              preview={medium.file.preview}
              type={medium.type}
              onDelete={() => onMediaDelete(index)}
              disabled={isLoading}
              key={index}
              totalMedia={formik.values.media.length}
              mediaNumber={index + 1}
            />
          ))}
        </div>
        <div className={styles.uploader}>
          <MediaUploader
            disabled={isLoading}
            onChangeFile={onFileSelect}
            pickerOpen={openPicker}
            onFileDialogCancelFn={() => setOpenPicker(false)}
          />
        </div>
      </div>
      <div className={styles.bottomContainer}>
        <IconButton onClick={() => setOpenPicker(true)}>
          <PictureIcon />
        </IconButton>
        {/* <IconButton onClick={() => setOpenPicker(true)}>
          <CameraIcon variant="outlined" />
        </IconButton> */}
      </div>
      {isCloseAlertDialogOpen && (
        <CreatePostMobileDialogCloseConfirmation
          onClose={() => setIsCloseAlertDialogOpen(false)}
          onLeave={() => onClose()}
        />
      )}
    </MobileModal>
  );
};

export default CreatePostMobileDialog;
